import { useState } from "react";
import { Check, Copy, Eye, EyeOff, KeyRound, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/shared";
import { ConfirmDialog } from "@/components/ConfirmDialog";

function KeyRow({ k, onRevoke }) {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(k.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 row-enter" data-testid={`api-key-row-${k.id}`}>
      <div className="sm:w-40 shrink-0">
        <div className="text-sm font-medium">{k.name}</div>
        <div className="text-xs text-zinc-500">{new Date(k.created_at).toLocaleDateString("pt-BR")}</div>
      </div>
      <code className="flex-1 font-mono text-xs bg-zinc-950 border border-zinc-800 px-3 py-2 rounded-sm truncate" data-testid={`api-key-value-${k.id}`}>
        {show ? k.key : `${k.key.slice(0, 9)}${"•".repeat(24)}`}
      </code>
      <div className="flex gap-1">
        <button onClick={() => setShow(!show)} data-testid={`api-key-toggle-${k.id}`} className="p-2 text-zinc-400 hover:text-white rounded-sm" title={show ? "Ocultar" : "Mostrar"}>{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
        <button onClick={copy} data-testid={`api-key-copy-${k.id}`} className="p-2 text-zinc-400 hover:text-white rounded-sm" title="Copiar">{copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}</button>
        <button onClick={onRevoke} data-testid={`api-key-revoke-${k.id}`} className="p-2 text-zinc-400 hover:text-red-400 rounded-sm" title="Revogar"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export default function ApiKeys({ db, keys = [], onChange }) {
  const [name, setName] = useState("");
  const [toRevoke, setToRevoke] = useState(null);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/databases/${db}/keys`, { name });
      toast.success(`Chave "${data.name}" criada — copie e guarde com segurança`);
      setName("");
      onChange?.();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const revoke = async () => {
    try {
      await api.delete(`/databases/${db}/keys/${toRevoke}`);
      toast.success("Chave revogada");
      setToRevoke(null);
      onChange?.();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div data-testid="api-keys">
      <form onSubmit={create} className="flex gap-2 mb-4">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome da chave (ex.: backend-prod)" data-testid="api-key-name-input" className="bg-transparent border-zinc-800 rounded-sm max-w-sm" />
        <Button type="submit" data-testid="api-key-create-button" className="bg-orange-600 hover:bg-orange-500 text-white rounded-sm btn-tactile"><Plus className="h-4 w-4 mr-1" /> Gerar chave</Button>
      </form>
      <p className="text-xs text-zinc-500 mb-4">Envie no cabeçalho <code className="font-mono text-zinc-300">X-Api-Key</code>. Cada chave dá acesso apenas às rotas <code className="font-mono text-zinc-300">/api/db/{db}/…</code> deste banco.</p>
      {keys.length === 0 ? (
        <EmptyState icon={KeyRound} title="Nenhuma chave de API" description="Gere uma chave para que suas aplicações usem este banco sem o login de admin." testId="api-keys-empty" />
      ) : (
        <div className="panel divide-y divide-zinc-800">{keys.map((k) => <KeyRow key={k.id} k={k} onRevoke={() => setToRevoke(k.id)} />)}</div>
      )}
      <ConfirmDialog open={!!toRevoke} onOpenChange={(o) => !o && setToRevoke(null)} title="Revogar chave?" description="Aplicações usando esta chave perderão o acesso imediatamente." confirmLabel="Revogar" onConfirm={revoke} testId="confirm-revoke-key" />
    </div>
  );
}
